import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-users.css";

/**
 * RAdminUsers — /admin/users
 *
 * Redesigned Admin Users screen (design-ref/admin/Users.html) with
 * functionality replicated 1:1 from the legacy
 * frontend/src/pages/AdminUsersPage.js:
 *   - GET /api/admin/admins on mount (auth via adminFetch — missing/expired
 *     token or 401 redirects to /admin/login); 403 → "You do not have
 *     permission to view admin users"; other errors → data.detail fallback;
 *     network failure → "Network error occurred"
 *   - client-side search across name + email (case-insensitive)
 *   - card grid: colored top accent + avatar initials (purple = Full Access,
 *     blue = Live Dashboard Only), name, email, Active/Inactive pill
 *     (is_active), access-level badge, Created + Last login (en-AU,
 *     "Never" when null), Edit Access + Delete
 *   - access-level detection preserved exactly: permissions array of exactly
 *     ["live_dashboard.read"] → "Live Dashboard Only", anything else →
 *     "Full Access"
 *   - Add modal: Full Name*, Email*, Password* (min 8 characters),
 *     Access Level* (full | live_dashboard); client guard "Please fill in
 *     all required fields"; POST /api/admin/admins with the full form body
 *     {name, email, password, role:'admin', permissions}; on success: close,
 *     reset form, refetch
 *   - Edit Access modal: identity (name/email) read-only, only Access Level
 *     editable; PUT /api/admin/admins/{id} with {permissions}; on success:
 *     close, clear selection, refetch
 *   - Delete confirm (design's ConfirmModal replaces the legacy modal) →
 *     DELETE /api/admin/admins/{id}; on success refetch (backend errors such
 *     as "Cannot delete your own account" surface via data.detail); modal
 *     stays open on failure, same as legacy
 *   - legacy inline success/error Alerts (3s auto-clear) → toast pills
 *     (auto-dismiss); error copy preserved incl. data.detail fallbacks
 *   - legacy "Back to Dashboard" button is superseded by the shell sidebar
 *   - backend also supports an 'order_management' level (shown in the design
 *     mock) — intentionally NOT surfaced, per the handoff spec ("not surfaced
 *     in UI today") and because PUT /api/admin/admins/{id} cannot set it
 */

const UsersIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="#1bab88"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 20a5.5 6 0 0111 0" />
    <circle cx="17" cy="9" r="2.4" />
    <path d="M15.5 20a5 5 0 016.5-4.3" />
  </svg>
);

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "admin",
  permissions: "full",
};

/* Legacy detection, preserved exactly: only an admin whose permissions array
   is exactly ["live_dashboard.read"] counts as "Live Dashboard Only". */
const isLiveDashboardOnly = (admin) =>
  !!(
    admin.permissions &&
    admin.permissions.length === 1 &&
    admin.permissions[0] === "live_dashboard.read"
  );

const initialsOf = (name) => {
  if (!name) return "?";
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
};

const RAdminUsers = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states (legacy: showAddModal / showEditModal / showDeleteModal)
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null); // edit target
  const [deletingAdmin, setDeletingAdmin] = useState(null); // delete target

  // Form state (legacy shape — the POST body sends this object verbatim)
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Fetch admin users — same handling as the legacy fetchAdmins()
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch("/api/admin/admins", { navigate });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      } else if (response.status === 403) {
        toast.error("You do not have permission to view admin users");
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to fetch admin users");
      }
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Error fetching admins:", err);
        toast.error("Network error occurred");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // Create new admin user — same guard + POST as legacy handleCreateAdmin()
  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const response = await adminFetch("/api/admin/admins", {
        method: "POST",
        body: JSON.stringify(formData),
        navigate,
      });
      if (response.ok) {
        toast.success("✓ Admin user created");
        setFormData(EMPTY_FORM);
        setShowAddModal(false);
        fetchAdmins();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create admin user");
      }
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Error creating admin:", err);
        toast.error("Network error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  // Update admin user access level — same PUT body as legacy ({permissions})
  const handleUpdateAdmin = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const response = await adminFetch(
        `/api/admin/admins/${selectedAdmin.id}`,
        {
          method: "PUT",
          body: JSON.stringify({ permissions: formData.permissions }),
          navigate,
        }
      );
      if (response.ok) {
        toast.success("✓ Access level updated");
        setShowEditModal(false);
        setSelectedAdmin(null);
        fetchAdmins();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to update admin user");
      }
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Error updating admin:", err);
        toast.error("Network error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  // Delete admin user — same DELETE as legacy handleDeleteAdmin();
  // like the legacy modal, only closes on success.
  const handleDeleteAdmin = async () => {
    if (!deletingAdmin) return;
    try {
      setDeleteBusy(true);
      const response = await adminFetch(
        `/api/admin/admins/${deletingAdmin.id}`,
        { method: "DELETE", navigate }
      );
      if (response.ok) {
        toast.success("Admin user deleted");
        setDeletingAdmin(null);
        fetchAdmins();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to delete admin user");
      }
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Error deleting admin:", err);
        toast.error("Network error occurred");
      } else {
        setDeletingAdmin(null);
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  // Open Add modal — legacy resets the form first, then opens
  const openAddModal = () => {
    setFormData(EMPTY_FORM);
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  // Open Edit modal — legacy permission mapping preserved
  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: "",
      role: admin.role,
      permissions: isLiveDashboardOnly(admin) ? "live_dashboard" : "full",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedAdmin(null);
  };

  // Last login / created formatting — "Never" when null (legacy formatDate)
  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Design shows the created stamp as date-only ("Created 2 Jan 2024")
  const formatDateShort = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Client-side filter — same predicate as the legacy page
  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const subtitle = loading
    ? null
    : searchTerm
      ? `${filteredAdmins.length} of ${admins.length} admin users`
      : `${admins.length} admin users`;

  return (
    <AdminShell
      active="users"
      pageClass="pg-admin-users"
      eyebrow="System"
      title="Admin Users"
      subtitle={subtitle}
    >
      {/* toolbar: search + Add Admin User */}
      <div className="usr-toolbar">
        <input
          className="usr-search"
          placeholder="Search users…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search users"
        />
        <button type="button" className="adm-btn" onClick={openAddModal}>
          + Add Admin User
        </button>
      </div>

      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading admin users…
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className="adm-empty">
          <div className="adm-empty-icon">
            <UsersIcon />
          </div>
          <div className="adm-empty-title">No admin users found</div>
          <div className="adm-empty-sub">
            {searchTerm
              ? "Try adjusting your search"
              : "Get started by adding your first admin user"}
          </div>
        </div>
      ) : (
        <div className="adm-grid3">
          {filteredAdmins.map((admin) => {
            const liveOnly = isLiveDashboardOnly(admin);
            return (
              <div key={admin.id} className="usr-card">
                <div
                  className={"usr-accent" + (liveOnly ? " usr-accent--blue" : "")}
                />
                <div className="usr-body">
                  <div className="usr-head">
                    <span
                      className={
                        "usr-avatar" + (liveOnly ? " usr-avatar--blue" : "")
                      }
                    >
                      {initialsOf(admin.name)}
                    </span>
                    <div className="usr-id">
                      <div className="usr-name">{admin.name}</div>
                      <div className="usr-email">{admin.email}</div>
                    </div>
                    <span
                      className={
                        "adm-chip " +
                        (admin.is_active
                          ? "adm-chip--teal"
                          : "usr-chip--inactive")
                      }
                    >
                      {admin.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {/* access-level badge — purple Full / blue Live Dashboard */}
                  <div className="usr-access">
                    <span
                      className={
                        "adm-chip " +
                        (liveOnly ? "adm-chip--blue" : "adm-chip--purple")
                      }
                    >
                      {liveOnly ? "Live Dashboard Only" : "Full Access"}
                    </span>
                  </div>

                  <div className="usr-meta">
                    Created {formatDateShort(admin.created_at)}
                    <br />
                    Last login {formatDate(admin.last_login)}
                  </div>

                  <div className="usr-actions">
                    <button
                      type="button"
                      className="usr-btn"
                      onClick={() => openEditModal(admin)}
                    >
                      Edit Access
                    </button>
                    <button
                      type="button"
                      className="usr-btn usr-btn--delete"
                      onClick={() => setDeletingAdmin(admin)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Admin User modal */}
      {showAddModal && (
        <div className="adm-modal-overlay" onClick={closeAddModal}>
          <div
            className="adm-modal usr-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-modal-title">Add Admin User</div>
            <form onSubmit={handleCreateAdmin}>
              <label className="adm-field">
                <span className="adm-label">Full Name *</span>
                <input
                  className="adm-input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g. Jane Citizen"
                  required
                />
              </label>
              <label className="adm-field">
                <span className="adm-label">Email *</span>
                <input
                  className="adm-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="name@umoonaopalmine.com.au"
                  required
                />
              </label>
              <label className="adm-field">
                <span className="adm-label">
                  Password *{" "}
                  <span className="usr-label-hint">(min 8 characters)</span>
                </span>
                <input
                  className="adm-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
              </label>
              <label className="adm-field">
                <span className="adm-label">Access Level *</span>
                <select
                  className="adm-select"
                  value={formData.permissions}
                  onChange={(e) =>
                    setFormData({ ...formData, permissions: e.target.value })
                  }
                >
                  <option value="full">Full Access</option>
                  <option value="live_dashboard">Live Dashboard Only</option>
                </select>
              </label>
              <div className="adm-modal-actions">
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={closeAddModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="adm-btn" disabled={saving}>
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Access modal — identity read-only, only Access Level editable */}
      {showEditModal && selectedAdmin && (
        <div className="adm-modal-overlay" onClick={closeEditModal}>
          <div
            className="adm-modal usr-modal usr-modal--edit"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-modal-title">Edit Access</div>
            <form onSubmit={handleUpdateAdmin}>
              <div className="usr-readonly">{selectedAdmin.name}</div>
              <div className="usr-readonly usr-readonly--email">
                {selectedAdmin.email}
              </div>
              <label className="adm-field">
                <span className="adm-label">Access Level *</span>
                <select
                  className="adm-select"
                  value={formData.permissions}
                  onChange={(e) =>
                    setFormData({ ...formData, permissions: e.target.value })
                  }
                >
                  <option value="full">Full Access</option>
                  <option value="live_dashboard">Live Dashboard Only</option>
                </select>
              </label>
              <div className="adm-modal-actions">
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="adm-btn" disabled={saving}>
                  Save Access
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm (design pattern; same DELETE call as legacy) */}
      <ConfirmModal
        open={!!deletingAdmin}
        title="Delete this admin user?"
        body="They will immediately lose access. This cannot be undone."
        confirmLabel="Delete User"
        danger
        busy={deleteBusy}
        onConfirm={handleDeleteAdmin}
        onCancel={() => setDeletingAdmin(null)}
      />
    </AdminShell>
  );
};

export default RAdminUsers;
